import React, { useCallback, useMemo } from "react";
import { SiteInteractionPanelProps } from "./SiteInteractionPanel.types";
import { format, isToday } from "date-fns";
import { useRefreshRegister } from "../../components/RefreshProvider";
import useBreakpoints from "../../hooks/useBreakpoints";
import { usePostSiteInteractionGroupedQuery } from "../../app/useGeneratedApi";
import { OpenClickEventStatistics } from "../../app/GeneratedApi";
import { getLastPeriod } from "../../utils/dateUtils";

const formatMap: {
  [key in SiteInteractionPanelProps["groupTimeUnit"]]: string;
} = {
  HOUR: "hh:mm",
  DAY: "d MMM",
  MONTH: "MMM yy",
  YEAR: "yyyy",
};

export function useSiteInteractionPanel(props: SiteInteractionPanelProps) {
  const { from, to, groupTimeUnit } = props;
  const { mdUp } = useBreakpoints();

  const {
    data: siteInteractionGroupedData,
    refetch,
    isLoading: isLoadingGroupedStatistics,
    error,
  } = usePostSiteInteractionGroupedQuery({
    from,
    to,
    groupTimeUnit,
  });

  useRefreshRegister(async () => {
    await refetch();
  });

  const statistics = useMemo(() => {
    return (
      siteInteractionGroupedData?.siteInteractionStatistics || {
        siteInteractionStats: [],
        compareFormSubmissionsPercent: undefined,
        currentNumberOfFormSubmissions: 0,
      }
    );
  }, [siteInteractionGroupedData]);

  const xValueAccessor = useCallback((item: OpenClickEventStatistics) => {
    return item.eventTime!;
  }, []);

  const yValueAccessor = useCallback((item: OpenClickEventStatistics) => {
    return item.currentCount!;
  }, []);

  const formatDate = useCallback(
    (date: string | Date) => {
      const dateObj = typeof date === "string" ? new Date(date) : date;
      return format(dateObj, formatMap[groupTimeUnit] || "MMM d");
    },
    [groupTimeUnit]
  );

  return {
    statistics,
    yValueAccessor,
    xValueAccessor,
    formatDate,
    mdUp,
    isLoadingGroupedStatistics,
    error,
    groupTimeUnit,
    lastPeriod: isToday(to || new Date())
      ? getLastPeriod(siteInteractionGroupedData?.compareDays || 0)
      : "period",
  };
}
